import type { TranslationKeys } from './types';

export const da: TranslationKeys = {
    // Navbar
    navbar: {
      schools: 'Skoler',
      howItWorks: 'Sådan virker det',
      aboutMe: 'Om mig',
      pricing: 'Priser',
      faqs: 'FAQ',
      memories: 'Minder',
      myAccount: 'Min konto',
    },
    // Home Page - HERO MAIN (H1, tagline, subtitle)
    home: {
      title: 'Find dine surf-fotos på få sekunder',
      tagline: 'Surf-fotografi på Lanzarote',
      subtitle: 'Upload et foto og find dine minder med det samme, uden at lede gennem hundredvis af billeder',
      cta: 'Find mine fotos',
      stats: {
        photos: 'Fotos',
        schools: 'Skoler',
        surfers: 'Glade surfere',
      },
    },
    // Schools Page - SURF SCHOOLS
    schools: {
      title: 'Surfskoler',
      subtitle: 'Vælg din surfskole for at finde dine fotos',
      viewPhotos: 'Se fotos',
      introTitle: 'Din skole, din dag, dine minder',
      descriptionMain: 'Vælg surfskolen, hvor du tog din lektion, og find din session organiseret efter dato.',
      descriptionSecondary: 'Daglige surf-fotosessioner i Famara og La Santa.',
      loading: 'Indlæser skoler...',
      apiUnavailable:
        'Vi kunne ikke nå serveren (timeout eller fejl). Viser standard-skolelisten — prøv igen om et par minutter.',
      retry: 'Prøv igen',
      otherSchools: 'ANDRE SKOLER',
      valueHighQuality: 'Fotos i høj kvalitet',
      valueInstantDownload: 'Øjeblikkelig download',
      valueSecurePayment: 'Sikker betaling',
    },
    // How It Works Page - PHOTO ACCESS (SELFIE)
    howItWorks: {
      title: 'Find dine fotos sikkert',
      subtitle: 'Upload en selfie, så vores system automatisk kan identificere kun dine fotos.',
      step1: {
        title: 'Vælg din surfskole',
        description: 'Vælg skolen, hvor du tog din lektion',
      },
      step2: {
        title: 'Vælg datoen for din session',
        description: 'Vælg den dag, du surfede',
      },
      step3: {
        title: 'Upload en selfie',
        description: 'Vores system identificerer automatisk kun dine fotos',
      },
      step4: {
        title: 'Se dine fotos',
        description: 'Se alle fotos, hvor du optræder',
      },
      step5: {
        title: 'Tilføj dine minder',
        description: 'Vælg de fotos, du vil beholde',
      },
      step6: {
        title: 'Betal sikkert',
        description: 'Betal hurtigt og sikkert',
      },
      step7: {
        title: 'Øjeblikkelig download',
        description: 'Få dine fotos med det samme uden vandmærke',
      },
      smartSearch: {
        title: 'Smart søgning',
        description: 'Denne proces sikrer dit privatliv og forhindrer, at andre kan få adgang til eller købe fotos, der ikke tilhører dem.',
      },
      cta: 'Find dine fotos',
    },
    // About Me Page - ABOUT ME
    aboutMe: {
      label: 'Om mig',
      title: 'Fanger dine bedste',
      titleHighlight: 'øjeblikke i vandet',
      intro: 'Jeg er <strong>Germán Abreu</strong>, fotograf med speciale i surf og vandsport på Lanzarote. Jeg bor i La Santa og arbejder primært i Famara og andre surfspots på øen.',
      paragraph1: 'Efter mange år med at fotografere surfere og elever fra surfskoler opdagede jeg altid det samme problem: at finde sine fotos blandt hundredvis af billeder var langsomt og upraktisk.',
      paragraph2: 'Det var sådan <strong className="brand-name">Surf Photo Lanzarote</strong> opstod — en enkel, hurtig og sikker måde for hver person at finde kun sine egne fotos, uden endeløs søgning og med respekt for privatlivet.',
      mission: 'Mit mål er, at du tager et autentisk minde med hjem fra din oplevelse i vandet. For bølger forsvinder… men minderne bliver.',
      stats: {
        experience: '+8 år',
        experienceLabel: 'Erfaring',
        schools: '+7',
        schoolsLabel: 'Partnerskoler',
      },
    },
    // Pricing Page
    pricing: {
      title: 'Priser',
      subtitle: 'Vælg den mulighed, der passer bedst til dig',
      plan1: {
        title: 'Dit bedste minde',
        description: 'Et enkelt billede i høj kvalitet',
        details: [
          'Øjeblikkelig download',
          'Høj opløsning',
          'JPG-format',
          'Egnet til print',
          'Uden vandmærke',
        ],
      },
      plan2: {
        title: 'Alle minder fra din session',
        description: 'Alle fotos af samme person på samme dag.',
        details: [
          'Alle fotos fra dagen',
          'Øjeblikkelig download',
          'Høj opløsning',
          'JPG-format',
          'Uden vandmærke',
        ],
      },
      plan3: {
        title: 'Ekstra dag',
        description: 'Tilføj endnu en dag til din pakke.',
        details: [
          'Alle fotos fra den ekstra dag',
          'Øjeblikkelig download',
          'Høj opløsning',
          'Uden vandmærke',
        ],
      },
      plan4: {
        title: 'Ekstra person',
        description: 'Tilføj fotos af en anden person. De kan være fra samme dag eller en anden dag.',
        details: [
          'Alle fotos af en anden person',
          'Øjeblikkelig download',
          'Høj opløsning',
          'Uden vandmærke',
        ],
      },
      includes: 'Inkluderer',
      note: 'Alle priser inkluderer øjeblikkelig download uden vandmærke',
    },
    // FAQ Page
    faqs: {
      title: 'Ofte stillede spørgsmål',
      subtitle: 'Find svar på de mest almindelige spørgsmål om, hvordan Surf Photo Lanzarote fungerer',
      contactText: 'Kan du ikke finde det svar, du leder efter?',
      contactLink: 'Kontakt os',
      questions: [
        {
          q: 'Hvordan finder jeg mine fotos?',
          a: 'Vælg din surfskole, dagen for din session, og upload en selfie. Hjemmesiden viser dig kun de fotos, hvor du optræder den dag.',
        },
        {
          q: 'Hvorfor skal jeg uploade en selfie?',
          a: 'Vi bruger ansigtsgenkendelse til at identificere dit ansigt og vise dig kun dine fotos, uden at skulle lede gennem hundredvis af billeder.',
        },
        {
          q: 'Hvad sker der med den selfie, jeg uploader?',
          a: 'Selfien bruges kun til at udføre søgningen for den dag og gemmes ikke eller bruges til andre formål.',
        },
        {
          q: 'Kan jeg søge efter fotos fra flere dage med én selfie?',
          a: 'Nej. Hver søgning foretages pr. dag. Hvis du surfede på forskellige dage, skal du uploade en selfie for hver session for at finde dine fotos.',
        },
        {
          q: 'Hvad gør jeg, hvis vi er to eller flere personer?',
          a: 'Hver person skal uploade sin egen selfie for at finde sine fotos. Du kan tilføje hver persons fotos til kurven og fortsætte uden problemer.',
        },
        {
          q: 'Hvorfor har fotos et vandmærke?',
          a: 'Fotos vises med vandmærke for at beskytte fotografens arbejde. Efter køb kan du downloade dem uden vandmærke.',
        },
        {
          q: 'Hvilken kvalitet har de downloadede fotos?',
          a: 'Fotos downloades i høj kvalitet, klar til personlig brug, sociale medier og print.',
        },
        {
          q: 'Hvor længe vil mine fotos være tilgængelige?',
          a: 'Fotos forbliver online i en begrænset periode. Vi anbefaler, at du downloader dem så hurtigt som muligt efter købet.',
        },
        {
          q: 'Kan jeg købe fotos fra mere end én dag?',
          a: 'Ja. Du kan få adgang til forskellige dage og købe fotos fra hver session uafhængigt.',
        },
        {
          q: 'Er fotos offentlige?',
          a: 'Nej. Fotos er organiseret efter skole og dag, så hver kunde kun kan få adgang til sin egen session.',
        },
        {
          q: 'Er betalingen sikker?',
          a: 'Ja. Alle betalinger behandles via Stripe, en sikker og pålidelig betalingsplatform.',
        },
        {
          q: 'Hvad gør jeg, hvis jeg ikke kan finde mig selv eller har et problem?',
          a: 'Hvis du ikke optræder på fotos eller har et problem, kan du kontakte os, så hjælper vi dig.',
        },
      ],
    },
    // School Days Page
    schoolDays: {
      title: 'Vælg en dag',
      subtitle: 'Vælg dagen for din surf-session',
      noPhotos: 'Ingen fotos tilgængelige for denne skole endnu.',
      back: 'Tilbage',
      searchByDate: 'Søg efter dato',
      search: 'Søg',
      availableDays: 'Tilgængelige dage',
      selectDaySubtitle: 'Vælg en dag for at finde dine fotos',
      loadingDays: 'Indlæser tilgængelige dage...',
      noDaysYet: 'Ingen dage tilgængelige for denne skole endnu.',
      noDaysDesc: 'Fotos er organiseret efter dag. Kom snart tilbage for nye sessioner.',
      photos: 'fotos',
      errorLoadingDays: 'Fejl ved indlæsning af tilgængelige dage',
      noPhotosForDate: 'Ingen fotos tilgængelige for den dato',
    },
    // Day Photos Page
    dayPhotos: {
      title: 'Dine surf-fotos',
      subtitle: 'Fotos fra',
      findTitle: 'Find dine fotos',
      findSubtitle: 'Vælg, om du vil uploade en selfie eller tænde kameraet, så vi kan finde dit ansigt.',
      clickToSelect: 'Klik for at vælge en selfie',
      orDragImage: 'eller træk et billede hertil',
      faceGuide: {
        hintStarting: 'Forbereder ansigtsguide…',
        hintNoFace: 'Placer dit ansigt inde i ovalen',
        hintMultiple: 'Kun én person foran kameraet',
        hintTooFar: 'Kom lidt tættere på',
        hintTooClose: 'Træk dig lidt tilbage',
        hintMoveLeft: 'Flyt dig lidt til venstre',
        hintMoveRight: 'Flyt dig lidt til højre',
        hintMoveUp: 'Løft ansigtet lidt',
        hintMoveDown: 'Sænk ansigtet lidt',
        hintLookStraight: 'Kig lige ind i kameraet',
        turnLeft: 'Drej hovedet til venstre og hold',
        turnRight: 'Drej nu hovedet til højre',
        circleHead: 'Lav en langsom cirkel med hovedet',
        coverage: 'Ansigt {{pct}}%',
        hintHoldStill: 'Perfekt, hold stille…',
        hintReady: 'Taget',
        cameraDenied: 'Kameratilladelse blev nægtet. Du kan uploade en selfie eller prøve igen.',
        choosePrompt: 'Hvordan skal vi finde dit ansigt?',
        uploadSelfie: 'Upload en selfie',
        uploadSelfieHint: 'Brug et foto, du allerede har',
        startCamera: 'Tænd kameraet',
        startCameraHint: 'Vi guider dig, så vi kan finde dit ansigt',
        backToChoice: 'Vælg en anden mulighed',
        capture: 'Tag billede',
        useGallery: 'Upload fra galleri',
        loadingDetector: 'Starter kamera…',
        galleryMultipleFaces: 'Upload en selfie med kun én person (ikke gruppebillede).',
        galleryNoFace: 'Intet ansigt fundet. Prøv en anden selfie.',
      },
      changePhoto: 'Skift foto',
      analyzing: 'Analyserer...',
      searchPhotos: 'Find mine fotos',
      errorReadFile: 'Fejl ved læsning af fil. Prøv venligst et andet billede.',
      errorSelectFirst: 'Vælg venligst et foto først',
      errorEmptyFile: 'Den valgte fil er tom. Vælg venligst et andet billede.',
      errorAnalyze: 'Fejl ved analyse af foto. Tjek at serveren kører.',
      yourSelfie: 'Din selfie',
      analyzingFace: 'Analyserer dit ansigt...',
      streamingMatchesSoFar: '{{count}} match fundet indtil videre — søger stadig…',
      slowPathHint:
        'denne mappe er endnu ikke indekseret, så vi sammenligner foto for foto (langsomt kun første gang).',
      autoIndexHint:
        'Opretter indeks i baggrunden — din næste søgning i denne mappe bliver meget hurtigere.',
      indexClustersHint:
        'Opretter ansigtsindeks (clusters)… det sker én gang pr. skole/dag — derefter bruger søgninger kun clusters.',
      searchDiagnostics:
        'Ansigtssøgning: {{path}} · tilstand {{mode}} · {{seconds}}s',
      searchingIn: 'Søger efter match i',
      analysisResults: 'Analyseresultater',
      matches: 'Match',
      photosAnalyzed: 'Analyserede fotos',
      percentage: 'Procent',
      photosFound: 'Fundne fotos',
      addAllMemories: 'Tilføj alle dine minder',
      addAllMemoriesFor35: 'Tilføj alle dine minder for {{price}}€',
      similarity: 'Lighed',
      buy: 'Køb',
      viewLarge: 'Se i fuld størrelse',
      noMatch: 'Intet match',
      moreItems: 'flere',
      uploadPhoto: 'Upload dit foto',
      uploadDescription: 'Upload et tydeligt foto af dit ansigt for at finde dine surf-fotos',
      selectFile: 'Vælg foto',
      findPhotos: 'Find mine fotos',
      results: 'fotos fundet',
      addToCart: 'Tilføj til kurv',
      noResults: 'Ingen fotos fundet. Prøv at uploade et andet foto.',
      savedSearchBanner: 'Viser din gemte søgning — alle matchede fotos fra din konto.',
      newSearchLink: 'Kør en ny søgning med en anden selfie',
      savedSearchLoading: 'Indlæser gemt søgning...',
      savedSearchNotFound: 'Gemt søgning ikke fundet. Du kan køre en ny søgning nedenfor.',
      partnerOffer: 'Tilbud',
      partnerOfferCta: 'Se tilbud',
      partnerOffersWhileSearching: 'Partnertilbud mens vi søger efter dine fotos',
    },
    // Checkout Success Page
    account: {
      loginTitle: 'Log ind',
      registerTitle: 'Opret din profil',
      loginLead: 'Få adgang til dine køb og gemte søgninger i op til 90 dage.',
      continueGoogle: 'Fortsæt med Google',
      orEmail: 'eller med email',
      displayName: 'Navn',
      email: 'Email',
      password: 'Adgangskode',
      passwordStrengthLabel: 'Adgangskodestyrke',
      strengthWeak: 'Svag',
      strengthFair: 'Middel',
      strengthGood: 'God',
      strengthStrong: 'Stærk',
      strengthHint: 'Brug store bogstaver, tal og symboler',
      signIn: 'Log ind',
      signUp: 'Opret konto',
      forgotPassword: 'Glemt adgangskode?',
      forgotPasswordTitle: 'Nulstil din adgangskode',
      forgotPasswordLead: 'Indtast din email, så sender vi dig et link til at vælge en ny adgangskode.',
      sendResetLink: 'Send nulstillingslink',
      resetLinkSent: 'Hvis der findes en konto med den email, har vi sendt et nulstillingslink.',
      resetLinkError: 'Kunne ikke sende linket. Prøv igen.',
      backToSignIn: 'Tilbage til login',
      resetPasswordTitle: 'Vælg en ny adgangskode',
      resetPasswordLead: 'Indtast en ny adgangskode til din konto.',
      newPassword: 'Ny adgangskode',
      savePassword: 'Gem adgangskode',
      passwordResetSuccess: 'Adgangskode opdateret. Du kan logge ind nu.',
      passwordResetInvalid: 'Dette nulstillingslink er udløbet eller ugyldigt.',
      passwordResetError: 'Kunne ikke opdatere adgangskoden. Prøv igen.',
      passwordMismatch: 'Adgangskoderne matcher ikke.',
      confirmPassword: 'Bekræft adgangskode',
      noAccount: 'Har du ikke en konto?',
      haveAccount: 'Har du allerede en konto?',
      createAccount: 'Opret en',
      photographerCta: 'Er du fotograf?',
      photographerRegister: 'Opret fotografkonto',
      photographerPanel: 'Fotografpanel',
      verifyEmailSent: 'Tjek din email for at verificere din konto.',
      backHome: 'Tilbage til forsiden',
      oauthLoading: 'Logger dig ind...',
      oauthError: 'Login mislykkedes. Prøv igen.',
      oauthMissing: 'Ugyldigt loginlink.',
      emailVerifiedTitle: 'Email verificeret',
      emailVerifiedBody: 'Din profil er aktiv. Du kan logge ind og få adgang til dit galleri.',
      goToAccount: 'Gå til min konto',
      emailInvalidTitle: 'Ugyldigt link',
      emailInvalidBody: 'Verificeringslinket er udløbet eller ugyldigt.',
      checkEmailTitle: 'Tjek din email',
      checkEmailBody: 'Vi har sendt dig et link til at verificere din konto.',
      checkEmailSpam: 'Hvis du ikke kan se den, tjek spam eller reklamer.',
      resendVerification: 'Send verificeringsemail igen',
      resendSuccess: 'Hvis kontoen findes, har vi sendt en ny email.',
      resendError: 'Kunne ikke sende emailen igen. Prøv igen.',
      resendEmailRequired: 'Indtast din email for at sende igen.',
      emailNotConfigured:
        'Vi kunne ikke sende emailen fra serveren. Kontakt support eller prøv Google-login.',
      registerCreatedNoEmailTitle: 'Konto oprettet',
      registerCreatedNoEmailBody:
        'Din konto blev gemt, men vi kunne ikke sende verificeringsemailen endnu. Brug send igen nedenfor eller log ind med Google.',
      galleryTitle: 'Mit galleri',
      profileSubtitle: 'Dine køb og gemte søgninger, i op til 90 dage efter hver session.',
      tabProfile: 'Profil',
      hello: 'Hej',
      signOut: 'Log ud',
      tabPurchases: 'Køb',
      tabSearches: 'Gemte søgninger',
      statPurchases: 'Køb',
      statSearches: 'Søgninger',
      statPhotos: 'Fotos',
      editProfile: 'Rediger profil',
      saveProfile: 'Gem ændringer',
      profileSaved: 'Profil opdateret',
      profileSaveError: 'Kunne ikke gemme profil',
      changePhoto: 'Skift foto',
      removePhoto: 'Fjern foto',
      photoHint: 'JPG eller PNG, max 5 MB. Bruges som profilbillede.',
      photoUploadError: 'Kunne ikke uploade foto',
      photoRemoved: 'Foto fjernet',
      photoUploaded: 'Foto opdateret',
      emailReadonly: 'Email kan ikke ændres her',
      authGoogle: 'Google-konto',
      authEmail: 'Email og adgangskode',
      verified: 'Verificeret',
      photosInOrder: 'fotos',
      orderDate: 'Ordredato',
      downloadAvailable: 'Download tilgængelig',
      loadError: 'Kunne ikke indlæse dit galleri.',
      emailVerifyRequired:
        'Verificer din email for at få adgang til dit galleri, køb og downloads.',
      verifyEmailLink: 'Verificer email',
      noPurchases: 'Du har ingen køb endnu.',
      noSearches: 'Ingen gemte søgninger endnu.',
      order: 'Ordre',
      downloadZip: 'Download ZIP',
      expired: 'Downloadperiode udløbet',
      newSearch: 'Find fotos fra en anden dag',
      photosFound: 'fotos fundet',
      viewSession: 'Se session',
      searchSavedAt: 'Søgning gemt',
      loading: 'Indlæser...',
    },
    profilePrompt: {
      beforePayTitle: 'Opret din profil',
      beforePayBody:
        'Log ind med Google eller email for at downloade dine fotos når som helst i op til 90 dage efter sessionsdatoen.',
      afterPayTitle: 'Gem dine fotos på din profil',
      afterPayBody:
        'Uden profil kan du kun downloade én gang nu. Med profil har du adgang når som helst i 90 dage.',
      exitTitle: 'Gem dine resultater',
      exitBody: 'Opret en profil, så du ikke behøver at uploade din selfie igen næste gang.',
      createProfile: 'Opret profil',
      saveToProfile: 'Gem på min profil',
      saveResults: 'Gem resultater',
      continueGoogle: 'Fortsæt med Google',
      notNow: 'Ikke nu',
      downloadNowOnly: 'Download kun nu',
    },
    checkoutSuccess: {
      loading: 'Indlæser dine downloads...',
      paymentNotFound: 'Betaling ikke fundet',
      noDownloadData: 'Ingen downloaddata.',
      testHint: 'For at teste download-sektionen uden at betale, besøg:',
      backHome: 'Tilbage til forsiden',
      title: 'Betaling gennemført!',
      subtitle: 'Tak for dit køb. Her er dine fotos til download uden vandmærke.',
      hello: 'Hej',
      downloads: 'Downloads',
      downloadsIntro: 'Du kan downloade hvert foto i høj kvalitet ved at klikke på knappen eller billedet.',
      downloadAll: 'Download alle fotos',
      downloadingAll: 'Starter download…',
      downloadThis: 'Download dette foto',
      downloadingOne: 'Downloader…',
      downloadTitle: 'Download',
      total: 'Total',
      photo: 'foto',
      photos: 'fotos',
      exploreMore: 'Se flere skoler',
      missingSession: 'Mangler session_id eller testtilstand (test=1).',
      paymentIncomplete:
        'Vi kunne ikke indlæse betalingsdetaljerne lige nu, men hvis du gennemførte betalingen, gik den igennem — betal venligst ikke igen. Opdater denne side om et øjeblik, eller kontakt os med din email, så sender vi dig fotos.',
      loadError: 'Kunne ikke indlæse betalingsdetaljer.',
      unexpectedResponse: 'Uventet serversvar.',
      connectionError: 'Forbindelsesfejl. Tjek at backend kører.',
      downloadsOnThisPage:
        'Dine fotos er på denne side. Brug "Download alle fotos" eller hver "Download dette foto"-knap nedenfor.',
      guestProfileBenefit:
        'Du kan downloade dine fotos her når som helst med dette link. Opret en profil for at gemme dem i dit galleri og få adgang, når du vil.',
      createProfileCta: 'Opret profil',
      goToGallery: 'Mit galleri',
      downloadFailed: 'Download mislykkedes. Prøv "Download alle fotos" eller opdater denne side.',
      voucherEmail:
        'Vi har også sendt din købsbekræftelse med partner-voucher-PDF\'er via email.',
    },
    checkoutCancel: {
      title: 'Betaling annulleret',
      message: 'Der blev ikke trukket noget beløb. Du kan vende tilbage til kurven eller fortsætte med at browse.',
      backHome: 'Tilbage til forsiden',
      viewSchools: 'Se skoler',
    },
    // Cart Modal
    cart: {
      title: 'Dine minder',
      item: 'vare',
      items: 'varer',
      empty: 'Din kurv er tom',
      emptyDesc: 'Udforsk vores fotos og tilføj dine yndlingsminder',
      explorePhotos: 'Udforsk fotos',
      total: 'Total',
      checkout: 'Gå til betaling',
      remove: 'Fjern',
      beforePaymentDownloadNotice:
        'Dine fotos vil være tilgængelige til download på denne side med det samme efter betaling',
      emailPlaceholder: 'Din email',
      namePlaceholder: 'Dit navn',
      subtotal: 'Subtotal',
      igic: 'IGIC ({rate}%)',
      taxesNotIncludedNote: 'Priser ekskl. moms',
      discount: 'Rabat',
      processing: 'Behandler...',
      proceedToPayment: 'Fortsæt til betaling',
      continueShopping: 'Fortsæt med at shoppe',
      securePayment: 'Sikker betaling',
      instantDownload: 'Øjeblikkelig download',
      closeCart: 'Luk kurv',
      removeItem: 'Fjern vare',
      packComplete: 'Fuld pakke',
      photo: 'foto',
      photos: 'fotos',
      digitalFormat: 'Digitalt format',
      highResolution: 'Høj opløsning',
      errorEmailNameRequired: 'Indtast din email og dit navn for at fortsætte',
      errorInvalidEmail: 'Indtast en gyldig email (f.eks. dig@email.com)',
      errorEmptyCart: 'Din kurv er tom',
      errorCheckoutSession: 'Kunne ikke oprette betalingssession',
      errorNoPaymentUrl: 'Betalings-URL ikke modtaget. Tjek at Stripe er konfigureret.',
      errorPaymentFailed: 'Fejl ved behandling af betaling',
    },
    // Sticky purchase flow pricing bar
    purchasePricingBar: {
      ariaLabel: 'Priser',
      disclaimer:
        'Rabat på 2. pakke gælder, når begge pakker tilføjes til kurven sammen. Priser ekskl. moms (IGIC).',
      blocks: [
        {
          description: 'Dit bedste foto',
          badge: '1 foto',
        },
        {
          description: 'Alle dine fotos fra en session',
          badge: 'Fuld pakke',
        },
        {
          description: 'Ekstra session til dig',
          badge: '2. pakke',
        },
        {
          description: 'Alle fotos af en ven eller et familiemedlem',
          badge: '2. pakke',
        },
      ],
    },
    // Partners
    partners: {
      title: 'Partnerskoler',
    },
    // Footer
    footer: {
      brand: 'Surf Photo Lanzarote',
      description: 'Premium-markedsplads for surf-fotografi på Lanzarote',
      navigation: 'Navigation',
      home: 'Forside',
      schools: 'Skoler',
      howItWorks: 'Sådan virker det',
      pricing: 'Priser',
      legal: 'Juridisk',
      legalNotice: 'Juridisk meddelelse',
      privacy: 'Privatlivspolitik',
      terms: 'Vilkår og betingelser',
      contact: 'Kontakt',
      email: 'info@surfphotolanzarote.com',
      location: 'Lanzarote, De Kanariske Øer',
      rights: 'Alle rettigheder forbeholdes',
    },
    // Common
    common: {
      loading: 'Indlæser...',
      error: 'Der opstod en fejl',
      close: 'Luk',
      cancel: 'Annuller',
      confirm: 'Bekræft',
      save: 'Gem',
      delete: 'Slet',
      edit: 'Rediger',
      back: 'Tilbage',
    },
  };
